import { Component, OnInit, signal } from '@angular/core';
import { AngularModule } from '../../../../../modules/angular.module';
import { NavigationService } from '../../../../../services/navigation.service';
import { ZorroModule } from '../../../../../modules/zorro.module';
import { SetupWizardPayloadObjectKeys, SetupWizardState } from '@mira-avant/mtlibs';
import { SetupWizard } from '../../../../../services/setup-wizard';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs/internal/Subscription';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzProgressModule } from 'ng-zorro-antd/progress';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { combineLatest, firstValueFrom, from, of } from 'rxjs';
import { AuthService } from '../../../../../services/auth.service';

interface Breadcrumb {
	name: SetupWizardState;
	label: string;
	route: string[];
	completed: boolean;
}

@Component({
	selector: 'app-setup-wizard-breadcrumbs',
	imports: [AngularModule, ZorroModule, NzStepsModule, NzProgressModule, NzTooltipModule],
	standalone: true,
	templateUrl: './setup-wizard-breadcrumbs.html',
	styleUrl: './setup-wizard-breadcrumbs.scss',
})
export class SetupWizardBreadcrumbs implements OnInit {
	public breadcrumbs = signal<Breadcrumb[]>([]);
	public activeState = signal<SetupWizardState | undefined>(undefined);
	public SetupWizardState = SetupWizardState;
	public clientUuid = signal<string | undefined>(undefined);
	private sub?: Subscription;
	public currentStep = signal<number>(0);
	public accountCompletedPercent = signal<number>(0);

	constructor(
		public nav: NavigationService,
		public setupWizard: SetupWizard,
		private route: ActivatedRoute,
		private authService: AuthService
	) {
		this.sub = combineLatest([
			this.route.params,
			this.setupWizard.preview$,
			from(this.authService.isDistrictAdmin$()),
		]).subscribe(([params, preview, isDistrictAdmin]) => {
			this.clientUuid.set(params['clientUuid']);

			this.activeState.set(params['state']);

			this.updateBreadcrumbs(isDistrictAdmin);
			this.updateAccountCompletedPercent();
		});
	}

	ngOnInit() {}

	private updateBreadcrumbs(isDistrictAdmin: boolean) {
		const clientUuid = this.clientUuid();

		if (!clientUuid) {
			console.warn('Client UUID is not defined');
			return;
		}

		const dashboardCrumb = {
			name: SetupWizardState.DASHBOARD,
			label: 'Dashboard',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.DASHBOARD, clientUuid],
			completed: true,
		};

		const accountCrumb = {
			name: SetupWizardState.EDIT_STEPS_ACCOUNT,
			label: 'Account',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.EDIT_STEPS_ACCOUNT, clientUuid],
			completed: true,
		};

		const locationsCrumb = {
			name: SetupWizardState.EDIT_STEPS_LOCATIONS,
			label: 'Locations',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.EDIT_STEPS_LOCATIONS, clientUuid],
			completed: this.setupWizard.preview$.value.payload[SetupWizardPayloadObjectKeys.locations].length > 0,
		};

		const usersCrumb = {
			name: SetupWizardState.EDIT_STEPS_USERS,
			label: 'Users',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.EDIT_STEPS_USERS, clientUuid],
			completed: this.setupWizard.preview$.value.payload[SetupWizardPayloadObjectKeys.users].length > 0,
		};
		const coursesCrumb = {
			name: SetupWizardState.EDIT_STEPS_COURSES,
			label: 'Courses',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.EDIT_STEPS_COURSES, clientUuid],
			completed: this.setupWizard.preview$.value.payload[SetupWizardPayloadObjectKeys.courses].length > 0,
		};
		const sectionsCrumb = {
			name: SetupWizardState.EDIT_STEPS_SECTIONS,
			label: 'Sections',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.EDIT_STEPS_SECTIONS, clientUuid],
			completed: this.setupWizard.preview$.value.payload[SetupWizardPayloadObjectKeys.sections].length > 0,
		};
		const unitsCrumb = {
			name: SetupWizardState.EDIT_STEPS_UNITS,
			label: 'Units',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.EDIT_STEPS_UNITS, clientUuid],
			completed: this.setupWizard.preview$.value.payload[SetupWizardPayloadObjectKeys.units].length > 0,
		};
		const lessonsCrumb = {
			name: SetupWizardState.EDIT_STEPS_LESSONS,
			label: 'Lessons',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.EDIT_STEPS_LESSONS, clientUuid],
			completed: this.setupWizard.preview$.value.payload[SetupWizardPayloadObjectKeys.lessons].length > 0,
		};
		const activitiesCrumb = {
			name: SetupWizardState.EDIT_STEPS_ACTIVITIES,
			label: 'Activities',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.EDIT_STEPS_ACTIVITIES, clientUuid],
			completed: this.setupWizard.preview$.value.payload[SetupWizardPayloadObjectKeys.activities].length > 0,
		};
		const contentsCrumb = {
			name: SetupWizardState.EDIT_STEPS_CONTENTS,
			label: 'Contents',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.EDIT_STEPS_CONTENTS, clientUuid],
			completed: this.setupWizard.preview$.value.payload[SetupWizardPayloadObjectKeys.contents].length > 0,
		};
		const previewJobCrumb = {
			name: SetupWizardState.PREVIEW_JOB,
			label: 'Preview & Submit',
			route: ['/', 'my', 'admin', 'setup-wizard', SetupWizardState.PREVIEW_JOB, clientUuid],
			completed: true,
		};

		const BREADCRUMBS: Breadcrumb[] = !isDistrictAdmin // aka is admin
			? [
					dashboardCrumb,
					accountCrumb,
					locationsCrumb,
					sectionsCrumb,
					usersCrumb,
					coursesCrumb,
					unitsCrumb,
					lessonsCrumb,
					activitiesCrumb,
					contentsCrumb,
					previewJobCrumb,
				]
			: [dashboardCrumb, accountCrumb, locationsCrumb, coursesCrumb, sectionsCrumb, usersCrumb, previewJobCrumb];

		const breadcrumbs: Breadcrumb[] = BREADCRUMBS.map(crumb => ({
			name: crumb.name,
			route: crumb.route,
			label: crumb.label,
			completed: crumb.completed,
		}));

		this.breadcrumbs.set(breadcrumbs);

		//Calculate current state based on the index of crumb.name compared to activeState
		const currentIndex = breadcrumbs.findIndex(crumb => crumb.name === this.activeState());
		this.currentStep.set(currentIndex);
	}

	private updateAccountCompletedPercent() {
		//Scan over each breadcrumb - the total number of breadcrumbs that are completed
		//steps and each crumb.completed counts as a part of the percentage
		const breadcrumbs = this.breadcrumbs();
		if (!breadcrumbs || breadcrumbs.length === 0) {
			this.accountCompletedPercent.set(0);
			return;
		}

		//Don't count certain crumbs like 'dashboard' and 'preview job' in the completion percentage
		const crumbsToCalculate = breadcrumbs.filter(
			crumb => crumb.name !== SetupWizardState.PREVIEW_JOB && crumb.name !== SetupWizardState.DASHBOARD
		);

		const completedCount = crumbsToCalculate.filter(crumb => crumb.completed).length;
		const percent = (completedCount / crumbsToCalculate.length) * 100;
		this.accountCompletedPercent.set(Math.round(percent));
	}

	navigate(route: string[]) {
		this.nav.navigate(route);
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
